import React, { useEffect, useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { ThreeDots } from "react-loader-spinner";
import { FiFileText, FiDollarSign, FiBriefcase, FiEdit2, FiLayers } from "react-icons/fi";
import { AiOutlineEye } from "react-icons/ai";
import { getContractApi } from "../../../../Api/ClientWork/ClientWork";
import ActionButton from "../../../Common/ActionButton";
import CommonDetailModal from "../../../Common/CommonDetailModal";
import "../../../Accounts/Contracts/Contract/ContractDetailModal.css";
import "../Clients/ClientDetailModal.css";

/**
 * ContractDetailModal (ClientWork Contracts Screen)
 * Design & Layout identical to ClientDetailModal
 * Fields strictly mapped to Client Contract entity
 */
function ContractDetailModal({ isOpen, onClose, contractData, contractId }) {
  const history = useHistory();
  const [contract, setContract] = useState(contractData || null);
  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    const id = contractId || contractData?.Id;
    if (!id) {
      if (contractData) {
        setContract(contractData);
      }
      return;
    }

    let isMounted = true;
    (async () => {
      if (!contractData) {
        setLoading(true);
      }
      try {
        const response = await getContractApi(id);
        if (isMounted && response?.data?.IsSuccess && response.data.Data) {
          setContract(response.data.Data);
        }
      } catch (err) {
        if (isMounted && contractData) {
          setContract(contractData);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [contractId, contractData]);

  // Extract attached files (PrimaryAttachment, PerformanceGuarantee, Documents)
  const attachedFilesList = useMemo(() => {
    const list = [];

    if (contract?.PrimaryAttachment) {
      list.push({
        name: contract.PrimaryAttachment,
        label: "Primary Agreement",
        url: contract.PrimaryAttachment,
        type: "Agreement / Contract Document",
      });
    }

    if (contract?.PerformanceGuarantee) {
      list.push({
        name: contract.PerformanceGuarantee,
        label: "Performance Guarantee",
        url: contract.PerformanceGuarantee,
        type: "Guarantee Document",
      });
    }

    if (Array.isArray(contract?.Documents)) {
      contract.Documents.forEach((doc, idx) => {
        if (typeof doc === "string") {
          list.push({
            name: doc,
            label: `Attachment ${idx + 1}`,
            url: doc,
            type: "Document",
          });
        } else if (doc?.name) {
          list.push({
            name: doc.name,
            label: doc.label || `Attachment ${idx + 1}`,
            url: doc.dataUrl || doc.url || doc.path || doc.name,
            size: doc.size || "—",
            type: doc.type || "Document",
          });
        }
      });
    }

    return list;
  }, [contract?.PrimaryAttachment, contract?.PerformanceGuarantee, contract?.Documents]);

  if (!isOpen) {
    return null;
  }

  const CompactMetric = ({
    label,
    value,
    spanClass = "col-span-1",
    noLeftBorder = false,
    status = false,
  }) => (
    <div className={`client-detail-field ${spanClass} ${noLeftBorder ? "no-left-border" : ""}`}>
      <div className="contract-detail-field__label">{label}</div>
      <div className={status ? "contract-detail-status" : "contract-detail-field__value"}>
        {value != null && value !== "" ? value : "—"}
      </div>
    </div>
  );

  const DetailSectionHeader = ({ title, Icon, rightContent }) => (
    <div
      className="contract-detail-card__header d-flex justify-content-between align-items-center"
      style={{ minHeight: "44px", padding: "8px 14px" }}
    >
      <div className="d-flex align-items-center" style={{ gap: "9px" }}>
        <span className="contract-detail-card__icon">{Icon && <Icon />}</span>
        <b className="m-0" style={{ fontSize: "14px" }}>
          {title}
        </b>
      </div>
      {rightContent && <div className="d-flex align-items-center">{rightContent}</div>}
    </div>
  );

  const renderStatusChip = (status) => {
    const isActive = (status || "").toLowerCase() === "active";
    return (
      <span
        className="badge"
        style={{
          backgroundColor: isActive ? "#d4edda" : "#fff3cd",
          color: isActive ? "#155724" : "#856404",
          border: isActive ? "1px solid #c3e6cb" : "1px solid #ffeeba",
          borderRadius: "14px",
          padding: "4px 12px",
          fontSize: "11px",
          fontWeight: 700,
          display: "inline-flex",
          alignItems: "center",
          textTransform: "capitalize",
          lineHeight: 1.2,
        }}
      >
        <span
          style={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            backgroundColor: isActive ? "#28a745" : "#ffc107",
            marginRight: "6px",
            display: "inline-block",
          }}
        />
        {status || "Active"}
      </span>
    );
  };

  const handleOpenImage = (doc) => {
    setPreviewImage(doc);
  };

  // Calculated values
  const contractValueNum = Number(contract?.ContractValue) || 0;
  const advanceDepositNum = Number(contract?.AdvanceDeposit) || 0;
  const retentionRateNum = Number(contract?.RetentionPercentage) || 0;
  const retentionAmountCalc =
    contractValueNum > 0 && retentionRateNum > 0
      ? Math.round((contractValueNum * retentionRateNum) / 100)
      : 0;

  const whtRateNum = Number(contract?.IncomeTaxWhtRate) || 0;
  const whtAmountCalc =
    contractValueNum > 0 && whtRateNum > 0 ? Math.round((contractValueNum * whtRateNum) / 100) : 0;

  return (
    <>
      <CommonDetailModal
        isOpen={isOpen}
        onClose={onClose}
        title="Client Contract Detail"
        maxWidth="1040px"
        contentClassName="modal-content-screen1 contract-detail-modal client-detail-modal"
        topContent={
          <div className="client-detail-content">
            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "180px" }}
              >
                <ThreeDots color="#5cd1d0" height={40} width={40} />
              </div>
            ) : (
              <>
                {/* ── ROW 1: Contract Identification & Financial Terms ── */}
                <div className="row mt-2">
                  {/* Card 1: Contract & Project Details */}
                  <div className="col-lg-6 col-12 mb-3 client-detail-column">
                    <div className="contract-detail-card h-100">
                      <DetailSectionHeader
                        title="Contract & Identity Details"
                        Icon={FiFileText}
                        rightContent={renderStatusChip(contract?.Status)}
                      />
                      <div className="client-detail-metrics">
                        <CompactMetric
                          label="Contract / Agreement No"
                          value={
                            contract?.ContractNo || (contract?.Id ? `#CNT-${contract.Id}` : "—")
                          }
                          spanClass="col-span-1"
                          noLeftBorder
                        />
                        <CompactMetric
                          label="Project Owner (Client)"
                          value={contract?.ClientName || contract?.OwnerName || "—"}
                          spanClass="col-span-2"
                        />
                        <CompactMetric
                          label="Verified Client NTN"
                          value={contract?.ClientNTN || contract?.NTN || "—"}
                          spanClass="col-span-1"
                          noLeftBorder
                        />
                        <CompactMetric
                          label="Contract Type"
                          value={contract?.ContractType || "Lump Sum / Fixed Price"}
                          spanClass="col-span-1"
                        />
                        <CompactMetric
                          label="Project Name"
                          value={contract?.ProjectName || "—"}
                          spanClass="col-span-1"
                        />
                        <CompactMetric
                          label="Contract Date"
                          value={
                            contract?.ContractDate
                              ? moment(contract.ContractDate).format("DD-MMM-YYYY")
                              : "—"
                          }
                          spanClass="col-span-1"
                          noLeftBorder
                        />
                        <CompactMetric
                          label="Target Completion Date"
                          value={
                            contract?.TargetCompletionDate
                              ? moment(contract.TargetCompletionDate).format("DD-MMM-YYYY")
                              : "—"
                          }
                          spanClass="col-span-2"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Commercial & Financial Terms */}
                  <div className="col-lg-6 col-12 mb-3 client-detail-column">
                    <div className="contract-detail-card h-100">
                      <DetailSectionHeader
                        title="Commercial & Financial Terms"
                        Icon={FiDollarSign}
                      />
                      <div className="client-detail-metrics">
                        <CompactMetric
                          label="Contract Value"
                          value={
                            contractValueNum > 0 ? `PKR ${contractValueNum.toLocaleString()}` : "—"
                          }
                          spanClass="col-span-1"
                          noLeftBorder
                        />
                        <CompactMetric
                          label="Advance / Mobilization Deposit"
                          value={
                            advanceDepositNum > 0
                              ? `PKR ${advanceDepositNum.toLocaleString()}`
                              : "PKR 0"
                          }
                          spanClass="col-span-1"
                        />
                        <CompactMetric
                          label="Retention Money (%):"
                          value={
                            contract?.RetentionPercentage != null ? (
                              <div>
                                <span>{contract.RetentionPercentage}%</span>
                                {retentionAmountCalc > 0 && (
                                  <small
                                    className="d-block text-muted"
                                    style={{ fontSize: "10.5px" }}
                                  >
                                    PKR {retentionAmountCalc.toLocaleString()}
                                  </small>
                                )}
                              </div>
                            ) : (
                              "—"
                            )
                          }
                          spanClass="col-span-1"
                        />
                        <CompactMetric
                          label="Income Tax WHT Rate (%):"
                          value={
                            contract?.IncomeTaxWhtRate != null ? (
                              <div>
                                <span>{contract.IncomeTaxWhtRate}%</span>
                                {whtAmountCalc > 0 && (
                                  <small
                                    className="d-block text-muted"
                                    style={{ fontSize: "10.5px" }}
                                  >
                                    PKR {whtAmountCalc.toLocaleString()}
                                  </small>
                                )}
                              </div>
                            ) : (
                              "—"
                            )
                          }
                          spanClass="col-span-1"
                          noLeftBorder
                        />
                        <CompactMetric
                          label="Defects Liability Period"
                          value={contract?.DefectsLiabilityPeriod || "12 Months"}
                          spanClass="col-span-1"
                        />
                        <CompactMetric
                          label="Mobilization Recovery Mode"
                          value={contract?.MobilizationRecoveryMode || "Standard"}
                          spanClass="col-span-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── ROW 2: Work Scope & Document Attachments (4 Columns Space) ── */}
                <div className="row">
                  <div className="col-12 mb-3 client-detail-column">
                    <div className="contract-detail-card h-100">
                      <DetailSectionHeader
                        title="Work Scope & Document Attachments"
                        Icon={FiBriefcase}
                      />
                      <div className="client-detail-metrics-4col">
                        {/* 4-Column Layout */}
                        <CompactMetric
                          label="Official Representative"
                          value={contract?.OfficialRepresentative || "—"}
                          spanClass="col-span-1"
                          noLeftBorder
                        />

                        <CompactMetric
                          label="Primary Agreement File"
                          value={
                            contract?.PrimaryAttachment ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-info d-inline-flex align-items-center mt-1"
                                style={{
                                  fontSize: "11px",
                                  fontWeight: 600,
                                  padding: "2px 8px",
                                  height: "26px",
                                  borderRadius: "6px",
                                  lineHeight: 1,
                                }}
                                title={`Click to view ${contract.PrimaryAttachment}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenImage({
                                    name: contract.PrimaryAttachment,
                                    label: "Primary Agreement",
                                    url: contract.PrimaryAttachment,
                                  });
                                }}
                              >
                                <AiOutlineEye className="mr-1" size="1.25em" />
                                <span>View Agreement</span>
                              </button>
                            ) : (
                              <span
                                className="text-muted d-block mt-1"
                                style={{ fontSize: "12px", fontStyle: "italic" }}
                              >
                                Not attached
                              </span>
                            )
                          }
                          spanClass="col-span-1"
                        />

                        <CompactMetric
                          label="Performance Guarantee"
                          value={
                            contract?.PerformanceGuarantee ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-info d-inline-flex align-items-center mt-1"
                                style={{
                                  fontSize: "11px",
                                  fontWeight: 600,
                                  padding: "2px 8px",
                                  height: "26px",
                                  borderRadius: "6px",
                                  lineHeight: 1,
                                }}
                                title={`Click to view ${contract.PerformanceGuarantee}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenImage({
                                    name: contract.PerformanceGuarantee,
                                    label: "Performance Guarantee",
                                    url: contract.PerformanceGuarantee,
                                  });
                                }}
                              >
                                <AiOutlineEye className="mr-1" size="1.25em" />
                                <span>View Guarantee</span>
                              </button>
                            ) : (
                              <span
                                className="text-muted d-block mt-1"
                                style={{ fontSize: "12px", fontStyle: "italic" }}
                              >
                                Not attached
                              </span>
                            )
                          }
                          spanClass="col-span-1"
                        />

                        <CompactMetric
                          label="Attached Files Count"
                          value={
                            attachedFilesList.length > 0 ? (
                              <span style={{ fontWeight: 600, color: "#075e5c" }}>
                                {attachedFilesList.length} file(s) available
                              </span>
                            ) : (
                              "None"
                            )
                          }
                          spanClass="col-span-1"
                        />

                        {/* Full Width Scope Description */}
                        <CompactMetric
                          label="Scope of Work / Description"
                          value={contract?.ScopeOfWork || "No detailed scope of work specified."}
                          spanClass="col-span-full"
                          noLeftBorder
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Attached Documents Table (if any) ── */}
                {attachedFilesList.length > 0 && (
                  <div className="row mt-2">
                    <div className="col-12 client-detail-column">
                      <div className="table-responsive contract-detail-table">
                        <div className="contract-detail-table__header">
                          <span className="d-flex align-items-center" style={{ gap: "8px" }}>
                            <FiLayers />
                            <span>
                              Attached Contract Agreements & Documents ({attachedFilesList.length})
                            </span>
                          </span>
                        </div>
                        <table className="table table-hover m-0 contract-detail-table__table">
                          <thead className="thead-light">
                            <tr>
                              <th style={{ width: "60px", textAlign: "center" }}>Sr</th>
                              <th>Document / Agreement</th>
                              <th>Category</th>
                              <th>Size / Status</th>
                              <th style={{ textAlign: "center", width: "160px" }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {attachedFilesList.map((doc, idx) => (
                              <tr key={idx}>
                                <td className="table_data text-center">{idx + 1}</td>
                                <td
                                  className="table_data font-weight-bold"
                                  style={{ color: "#075e5c" }}
                                >
                                  {doc.name || `Document-${idx + 1}`}
                                </td>
                                <td className="table_data">{doc.label || "Contract Document"}</td>
                                <td className="table_data">{doc.size || "Attached"}</td>
                                <td className="table_data text-center">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-info py-1 px-3 d-inline-flex align-items-center"
                                    style={{
                                      fontSize: "11px",
                                      fontWeight: 600,
                                      borderRadius: "6px",
                                    }}
                                    onClick={() => handleOpenImage(doc)}
                                  >
                                    <AiOutlineEye className="mr-1" size="1.25em" />
                                    <span>View File</span>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Footer Action Buttons ── */}
                <div className="row mt-3">
                  <div
                    className="col-12 d-flex flex-wrap justify-content-end"
                    style={{ gap: "10px" }}
                  >
                    <ActionButton
                      text="Edit Contract"
                      icon={FiEdit2}
                      variant="save"
                      onClick={() => {
                        onClose();
                        history.push(`/client-work/contracts/edit/${contract?.Id}`);
                      }}
                    />
                    <ActionButton text="Close" variant="grey" onClick={onClose} />
                  </div>
                </div>
              </>
            )}
          </div>
        }
      />

      {/* ── Lightbox Image Viewer Modal ── */}
      {previewImage && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.78)",
            zIndex: 1099,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
          onClick={() => setPreviewImage(null)}
        >
          <div
            style={{
              position: "relative",
              maxWidth: "850px",
              width: "100%",
              maxHeight: "90vh",
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              className="d-flex justify-content-between align-items-center px-3"
              style={{
                minHeight: "52px",
                backgroundColor: "#075e5c",
                color: "#ffffff",
              }}
            >
              <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                <FiFileText size={18} />
                <span style={{ fontWeight: 600, fontSize: "14px" }}>
                  {previewImage.name || "Contract File Preview"}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setPreviewImage(null)}
                style={{
                  background: "rgba(255,255,255,0.18)",
                  border: "none",
                  color: "#ffffff",
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  fontSize: "20px",
                  lineHeight: "26px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
                title="Close"
              >
                &times;
              </button>
            </div>

            {/* Modal Body / Image Viewer */}
            <div
              className="p-3 d-flex justify-content-center align-items-center"
              style={{
                minHeight: "260px",
                maxHeight: "calc(90vh - 120px)",
                overflow: "auto",
                backgroundColor: "#f4f8f8",
              }}
            >
              {previewImage.url &&
              /\.(png|jpe?g|webp|gif|svg)$/i.test(previewImage.name || previewImage.url) ? (
                <img
                  src={previewImage.url}
                  alt={previewImage.name || "Attached contract preview"}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                    borderRadius: "6px",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    const fallback = document.getElementById("contract-preview-fallback-msg");
                    if (fallback) {
                      fallback.style.display = "block";
                    }
                  }}
                />
              ) : null}

              <div
                id="contract-preview-fallback-msg"
                style={{
                  display:
                    previewImage.url &&
                    /\.(png|jpe?g|webp|gif|svg)$/i.test(previewImage.name || previewImage.url)
                      ? "none"
                      : "block",
                  textAlign: "center",
                  padding: "40px 20px",
                  color: "#556e6f",
                }}
              >
                <FiFileText size={48} className="mb-2 text-muted" />
                <h6 style={{ fontWeight: 600 }}>{previewImage.name || "Contract Document"}</h6>
                <p className="small mb-0 text-muted">
                  Attached file. PDF or document preview available for download.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div
              className="d-flex justify-content-end align-items-center p-2 px-3 bg-light border-top"
              style={{ gap: "10px" }}
            >
              {previewImage.url && (
                <a
                  href={previewImage.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={previewImage.name || "attachment"}
                  className="btn btn-sm btn-info d-inline-flex align-items-center"
                  style={{ fontSize: "12px", padding: "5px 12px" }}
                >
                  Download / Open Document
                </a>
              )}
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                style={{ fontSize: "12px", padding: "5px 14px" }}
                onClick={() => setPreviewImage(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ContractDetailModal;
